/**
 * usePlacementEvents — detects newly-arrived `placement` events in GameState
 * and expands them into per-territory animation entries.
 *
 * Contract:
 *   - The FIRST observed `events` array is treated as the "already seen"
 *     baseline so historical placements don't replay on mount.
 *   - Subsequent updates: any placement event with `ts` greater than the
 *     highest ts seen so far is considered "new".
 *   - A single `placement` event may cover multiple territories (bundled
 *     reinforce). Each territory gets its own independent entry.
 *   - A Set of already-animated ts values prevents re-fires on React
 *     StrictMode double-invoke, reconnects, or cache replays.
 *
 * Returns PlacementAnimationEntry[] — one entry per (event, territory) pair.
 */

import React from 'react'
import type { GameEvent } from '@warcarp/api-types'
import { useDeferredAnimation } from './useDeferredAnimation'

type PlacementEvent = Extract<GameEvent, { kind: 'placement' }>

export interface PlacementAnimationEntry {
  /** Unique key: combine ts + territoryId */
  territoryId: string
  armies: number
  /** Slot colour hex for the placing player (from PLAYER_COLORS). */
  playerColor: string
  /** epoch-ms when the animation was queued — used to clean up stale entries */
  startedAt: number
}

/**
 * Pure diff function — exported for unit tests.
 *
 * Returns all `placement` events whose `ts` is strictly greater than
 * `lastSeenTs`. Does NOT expand per-territory here — the hook does that.
 */
export function detectNewPlacements(
  nextEvents: GameEvent[] | undefined,
  lastSeenTs: number,
): PlacementEvent[] {
  if (!nextEvents || nextEvents.length === 0) return []
  return nextEvents.filter(
    (e): e is PlacementEvent => e.kind === 'placement' && e.ts > lastSeenTs,
  )
}

/** Duration the animation runs (ms). After this + buffer, the entry is removed.
 * Timeline: 60ms pop-in + 300ms hold + 400ms fade = 760ms. */
const ANIM_DURATION_MS = 900

export function usePlacementEvents(
  events: GameEvent[] | undefined,
  slotByPlayerId: Record<string, number>,
  playerColorHex: (slot: number) => string,
  /**
   * Retained for caller compatibility only — no longer used. The mount
   * baseline is always the max ts present at mount (see the effect below).
   * Keying off the WS-session start made phase remounts flush the whole
   * session's placements on the next state change; anchoring to maxTs fixes it.
   */
  viewerJoinedAt?: number,
): PlacementAnimationEntry[] {
  const lastSeenTsRef = React.useRef<number | null>(null)
  const animatedTsSet = React.useRef<Set<number>>(new Set())

  const [entries, setEntries] = React.useState<PlacementAnimationEntry[]>([])
  const defer = useDeferredAnimation()

  React.useEffect(() => {
    if (lastSeenTsRef.current === null) {
      // Baseline = highest ts present at mount; everything present now counts
      // as already-seen so a phase remount never replays the session's prior
      // placements. Only events arriving after mount animate. (Was
      // viewerJoinedAt-1, which flushed history on the next state change.)
      const baseline =
        events && events.length > 0
          ? Math.max(...events.map((e) => e.ts))
          : 0
      lastSeenTsRef.current = baseline
      // Pre-populate animatedTsSet with everything at/below the baseline so
      // the dedup guard doesn't fire if the same ts arrives again via WS push.
      if (events) {
        for (const e of events) {
          if (e.ts <= baseline) animatedTsSet.current.add(e.ts)
        }
      }
      return
    }

    const newPlacements = detectNewPlacements(events, lastSeenTsRef.current)
    if (newPlacements.length === 0) return

    const maxNewTs = Math.max(...newPlacements.map((e) => e.ts))
    lastSeenTsRef.current = Math.max(lastSeenTsRef.current, maxNewTs)

    const fresh = newPlacements.filter((e) => !animatedTsSet.current.has(e.ts))
    if (fresh.length === 0) return

    for (const e of fresh) animatedTsSet.current.add(e.ts)

    const now = Date.now()
    // Expand each placement event into one entry per territory.
    const newEntries: PlacementAnimationEntry[] = []
    for (const e of fresh) {
      const slot = slotByPlayerId[e.player] ?? 1
      const color = playerColorHex(slot)
      for (const p of e.placements) {
        newEntries.push({
          territoryId: p.territoryId,
          armies: p.armies,
          playerColor: color,
          startedAt: now,
        })
      }
    }

    // Deferred so the setState re-render does not compete with a tap gesture
    // still resolving on this frame — that is what gives the animation a clean
    // first frame on iOS. Scheduled through useDeferredAnimation, which cancels
    // only on UNMOUNT: cancelling in this effect's own cleanup is what silently
    // ate the placement badges, because the effect is keyed on `events` and the
    // callers pass `gameState.events` — a new array identity on every state
    // patch, so the next patch abandoned the dispatch before it could run.
    defer.afterInteractions(() => {
      setEntries((prev) => [...prev, ...newEntries])
    })

    defer.later(() => {
      const cutoff = now
      setEntries((prev) => prev.filter((e) => e.startedAt > cutoff))
    }, ANIM_DURATION_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events])

  return entries
}
